import React, { useState, useEffect, useContext } from 'react';
import { getTariffs, createTariff, updateTariff, activateTariff } from '../../services/tariffService';
import { AuthContext } from '../../context/AuthContext';
import { 
  Box, 
  Button, 
  Typography, 
  Container, 
  CircularProgress, 
  Alert, 
  Fade 
} from '@mui/material';
import { 
  ElectricBolt as ElectricIcon,
  Add as AddIcon 
} from '@mui/icons-material';
import TariffList from '../../components/TarifaEnergetica/TariffList';
import CreateTariffModal from '../../components/TarifaEnergetica/CreateTariffModal';
import EditTariffModal from '../../components/TarifaEnergetica/EditTariffModal';
import Navbar from '../../components/Navbar';
import VerticalMenu from '../../components/VerticalMenu';
import Footer from '../../components/Footer';
import { MovilidadElectricaProvider } from '../../context/MovilidadElectricaContext';

const TarifaEnergeticaContent = () => {
  const { user } = useContext(AuthContext);
  const isAdminUser = user?.is_admin;
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState(null);

  const fetchTariffs = async () => {
    try {
      setLoading(true);
      const placeId = sessionStorage.getItem("selectedPlaceId");
      const params = placeId ? { place_id: placeId } : {};
      const data = await getTariffs(params);
      setTariffs(data.data);
    } catch (err) {
      setError('Error al cargar las tarifas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTariffs();
  }, []);

  const handleCreate = async (tariffData) => {
    try {
      const placeId = tariffData.place_id ?? sessionStorage.getItem("selectedPlaceId");
      if (!placeId) {
        setError('Selecciona una sede antes de crear una tarifa');
        return;
      }

      await createTariff({ ...tariffData, place_id: Number(placeId) });
      fetchTariffs();
      setCreateModalOpen(false);
    } catch (error) {
      setError('Error al crear la tarifa');
    }
  };

  const handleUpdate = async (tariffId, tariffData) => {
    try {
      await updateTariff(tariffId, tariffData);
      fetchTariffs();
      setEditModalOpen(false);
    } catch (error) {
      setError('Error al actualizar la tarifa');
    }
  };


  const handleToggleActive = async (tariff) => {
    try {
      if (!tariff.is_active) {
        await activateTariff(tariff.tariff_id);
      } else {
        // Intento de desactivar la tarifa
        try {
          await updateTariff(tariff.tariff_id, { ...tariff, is_active: false });
        } catch (error) {
          // Si el backend devuelve un error, lo mostramos en una alerta
          if (error.response && error.response.data && error.response.data.detail) {
            alert(error.response.data.detail);
          } else {
            setError('Error al cambiar el estado de la tarifa');
          }
        }
      }
      fetchTariffs();
    } catch (error) {
      if (error.response && error.response.data && error.response.data.detail) {
        alert(error.response.data.detail);
      } else {
        setError('Error al cambiar el estado de la tarifa');
      }
    }
  };

  const openCreateModal = () => setCreateModalOpen(true);
  const closeCreateModal = () => setCreateModalOpen(false);

  const openEditModal = (tariff) => {
    setSelectedTariff(tariff);
    setEditModalOpen(true);
  };
  const closeEditModal = () => {
    setSelectedTariff(null);
    setEditModalOpen(false);
  };


  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="50vh"
        >
          <CircularProgress 
            size={60} 
            sx={{ color: '#002642' }}
          />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
        <Alert 
          severity="error" 
          sx={{ 
            borderRadius: 3,
            '& .MuiAlert-message': {
              fontSize: '1.1rem'
            }
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
      <Fade in={true} timeout={600}>
        <Box>
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 1.5, sm: 2 }, 
            mb: { xs: 2.5, sm: 4 }
          }}>
            <Box
              sx={{
                p: { xs: 1.25, sm: 1.5 },
                borderRadius: { xs: 2, sm: 3 },
                background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ElectricIcon sx={{ 
                color: '#FFFFFF', 
                fontSize: { xs: '1.75rem', sm: '2rem' } 
              }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#002642',
                  fontSize: { xs: '1.375rem', sm: '2rem', md: '2.5rem' },
                  mb: { xs: 0.25, sm: 0.5 },
                  lineHeight: { xs: 1.2, sm: 1.1 }
                }}
              >
                Gestión de Tarifas Energéticas
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#75787B',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  lineHeight: 1.3
                }}
              >
                Configura y administra las tarifas de energía eléctrica
              </Typography>
            </Box>
          </Box>

          {/* Create Button */}
          {isAdminUser && (
            <Box sx={{ mb: { xs: 2.5, sm: 4 } }}>
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                onClick={openCreateModal}
                sx={{
                  backgroundColor: '#002642',
                  color: '#FFFFFF',
                  py: { xs: 1.25, sm: 2 },
                  px: { xs: 2.5, sm: 4 },
                  borderRadius: { xs: 2, sm: 3 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: 600,
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { sm: 200 },
                  boxShadow: { xs: 2, sm: 3 },
                  '&:hover': {
                    backgroundColor: '#00577A',
                    boxShadow: { xs: 3, sm: 6 },
                    transform: { xs: 'none', sm: 'translateY(-2px)' }
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                Crear Nueva Tarifa
              </Button>
            </Box>
          )}

          {/* Tariffs List/Table */}
          <TariffList 
            tariffs={tariffs} 
            handleEdit={isAdminUser ? openEditModal : null} 
            handleToggleActive={isAdminUser ? handleToggleActive : null} 
          />

          {/* Modals */}
          <CreateTariffModal 
            open={createModalOpen} 
            handleClose={closeCreateModal} 
            handleCreate={handleCreate} 
          />
          {selectedTariff && (
            <EditTariffModal 
              open={editModalOpen} 
              handleClose={closeEditModal} 
              handleUpdate={handleUpdate} 
              tariff={selectedTariff} 
            />
          )}
        </Box>
      </Fade>
    </Container>
  );
};

const TarifaEnergetica = () => {
  return (
    <MovilidadElectricaProvider>
      <TarifaEnergeticaContent />
    </MovilidadElectricaProvider>
  );
};

export default TarifaEnergetica;
